import {
  DateOrNullOrUndefined,
  ForceMap,
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
} from 'src/app/shared/utils';
import { formatDateToYmd } from 'src/app/shared/utils/formatDate';
import { Objective } from './objective.v2.model';
import { parseYMDDate } from 'src/app/shared/utils/DateOrNullOrUndefined';

export class Caregiver {
  index: number = 0;
  nombre: string = '';
  criteria: string = '';
  initiation?: Date;
  caregiver_goal: string = '';
  current_status: string = '';
  outcome_measure: string = '';
  constructor(data: Partial<Caregiver> = {}) {
    this.index = NumberOrNullOrUndefined(data.index) ?? 0;
    this.nombre = StringOrNullOrUndefined(data.nombre) ?? '';
    this.criteria = StringOrNullOrUndefined(data.criteria) ?? '';
    this.initiation = DateOrNullOrUndefined(data.initiation);
    this.caregiver_goal = StringOrNullOrUndefined(data.caregiver_goal) ?? '';
    this.current_status = StringOrNullOrUndefined(data.current_status) ?? '';
    this.outcome_measure = StringOrNullOrUndefined(data.outcome_measure) ?? '';
  }
}

type GoalV2Input = Omit<Partial<GoalV2>, 'baseline_date' | 'end_date'> & {
  baseline_date?: Date | string | null;
  end_date?: Date | string | null;
};

export class GoalV2 {
  index?: number;
  id: number = 0;
  bip_id: number = 0;
  name: string = '';
  description: string = '';
  baseline_level: number = 0;
  baseline_date?: Date;
  end_date?: Date;
  initial_intensity: number = 0;
  current_intensity: number = 0;
  category: 'maladaptive' | 'sustitution' = 'maladaptive';
  status: 'active' | 'inactive' = 'inactive';
  created_at?: Date;
  updated_at?: Date;
  deleted_at?: Date;
  objectives: Objective[] = [];
  caregivers_training_goals?: Caregiver[];
  constructor(data: GoalV2Input = {}) {
    this.index = NumberOrNullOrUndefined(data.index);
    this.id = NumberOrNullOrUndefined(data.id) ?? 0;
    this.bip_id = NumberOrNullOrUndefined(data.bip_id) ?? 0;
    this.name = StringOrNullOrUndefined(data.name) ?? '';
    this.description = StringOrNullOrUndefined(data.description) ?? '';
    this.baseline_level = NumberOrNullOrUndefined(data.baseline_level) ?? 0;
    this.baseline_date = parseGoalDateOnlyValue(data.baseline_date);
    this.end_date = parseGoalDateOnlyValue(data.end_date);
    this.initial_intensity = NumberOrNullOrUndefined(data.initial_intensity) ?? 0;
    this.current_intensity = NumberOrNullOrUndefined(data.current_intensity) ?? 0;
    this.category = (StringOrNullOrUndefined(data.category) as 'maladaptive' | 'sustitution' | undefined) ?? 'maladaptive';
    this.status = (StringOrNullOrUndefined(data.status) as 'active' | 'inactive' | undefined) ?? 'inactive';
    this.created_at = DateOrNullOrUndefined(data.created_at);
    this.updated_at = DateOrNullOrUndefined(data.updated_at);
    this.deleted_at = DateOrNullOrUndefined(data.deleted_at);
    this.objectives = ForceMap(data.objectives ?? [], Objective);
    this.caregivers_training_goals = (data.caregivers_training_goals ?? []).map((item) => new Caregiver(item));
  }

  static getDefault(): GoalV2 {
    return new GoalV2({
      id: 0,
      bip_id: 0,
      name: '',
      description: '',
      baseline_level: 0,
      baseline_date: undefined,
      initial_intensity: 0,
      current_intensity: 0,
      category: 'maladaptive',
      status: 'inactive',
      objectives: [],
    });
  }
}

function parseGoalDateOnlyValue(value: Date | string | null | undefined): Date | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = formatDateToYmd(value);
  if (normalized) {
    return parseYMDDate(normalized);
  }

  return DateOrNullOrUndefined(value);
}
